// Sprint 2 — Reorganización de configuración
import { useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useToast } from "@/hooks/useToast";
import { useConfirm } from "@/hooks/useConfirm";

type BlockRow = { id: string; dateFrom: string; dateTo: string; display: string };

export function DashboardBlockedDates() {
  const { refresh } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<BlockRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");

  async function reload() {
    const r = await fetchWithSessionSync("/api/dashboard/schedule", { credentials: "include" }, refresh);
    if (!r.ok) return;
    const d = (await r.json()) as { blocked: BlockRow[] };
    setRows(d.blocked);
  }

  useEffect(() => {
    void reload();
  }, [refresh]);

  async function addRange(e: React.FormEvent) {
    e.preventDefault();
    if (!from || !to) return;
    setBusy(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/blocked-dates",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dateFrom: from, dateTo: to, reason: reason.trim() || null }),
      },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      toast.error("No se pudo agregar el bloqueo.");
      return;
    }
    setFrom("");
    setTo("");
    setReason("");
    toast.success("Fecha(s) bloqueada(s)");
    void reload();
  }

  async function del(id: string) {
    if (!(await confirm("¿Quitar este bloqueo?"))) return;
    setBusy(true);
    const r = await fetchWithSessionSync(`/api/dashboard/blocked-dates/${id}`, { method: "DELETE", credentials: "include" }, refresh);
    setBusy(false);
    if (!r.ok) {
      toast.error("No se pudo eliminar.");
      return;
    }
    toast.success("Eliminado");
    void reload();
  }

  if (!rows) {
    return (
      <DashboardPageShell eyebrow="Disponibilidad" title="Fechas bloqueadas">
        <LoadingFrame variant="light" minHeight={260} />
      </DashboardPageShell>
    );
  }

  const input = cx(cf.input, css({ h: "10", fontSize: "sm" }));

  return (
    <DashboardPageShell
      eyebrow="Disponibilidad"
      title="Fechas bloqueadas"
      description="Deshabilitá rangos de días en los que no querés recibir reservas online."
    >
      <form onSubmit={(e) => void addRange(e)} className={cx(cf.dashSurface, css({ p: "5", mb: "8", display: "grid", gap: "4" }))}>
        <p className={css({ fontWeight: "semibold", color: "#0f172a", fontSize: "sm" })}>Nuevo bloqueo</p>
        <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" }, gap: "4" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Desde</span>
            <input type="date" required className={input} value={from} onChange={(e) => setFrom(e.target.value)} disabled={busy} />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Hasta</span>
            <input type="date" required className={input} value={to} onChange={(e) => setTo(e.target.value)} disabled={busy} />
          </label>
        </div>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Motivo (opcional)</span>
          <input type="text" className={input} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
        </label>
        <button
          type="submit"
          disabled={busy}
          className={css({
            justifySelf: "start",
            px: "4",
            py: "2.5",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            color: "white",
            border: "none",
            cursor: busy ? "wait" : "pointer",
            bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
          })}
        >
          Bloquear fechas
        </button>
      </form>

      {rows.length === 0 ? (
        <p className={css({ fontSize: "sm", color: "#64748b" })}>No hay fechas bloqueadas.</p>
      ) : (
        <div className={css({ overflowX: "auto" })}>
          <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
            <thead>
              <tr className={css({ borderBottomWidth: "1px", borderColor: "#e2e8f0", textAlign: "left" })}>
                <th className={css({ py: "3", pr: "4", color: "#64748b", fontWeight: "semibold" })}>Desde</th>
                <th className={css({ py: "3", pr: "4", color: "#64748b", fontWeight: "semibold" })}>Hasta</th>
                <th className={css({ py: "3", pr: "4" })} />
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className={css({ borderBottomWidth: "1px", borderColor: "#f1f5f9" })}>
                  <td className={css({ py: "3", color: "#0f172a" })}>{b.dateFrom}</td>
                  <td className={css({ py: "3", color: "#0f172a" })}>{b.dateTo}</td>
                  <td className={css({ py: "3" })}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void del(b.id)}
                      className={css({
                        fontSize: "xs",
                        color: "#b91c1c",
                        bg: "transparent",
                        border: "none",
                        cursor: "pointer",
                        textDecoration: "underline",
                      })}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardPageShell>
  );
}
